import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Download, Instagram as InstagramIcon } from "lucide-react";
import { absUrl, isMobile, openInstagramCreate, saveImage, saveImages } from "./_helpers";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// One Day card on the public Kit delivery page. Renders Single / Carousel /
// Reel / Story formats with the right media block, caption, CTA, and the
// Copy + Open IG action row. Extracted from Kit.jsx.
export default function DayCard({
    entry,
    image,
    slides = [],
    reel = null,
    isPosted,
    onTogglePosted,
    registerRef,
    orderId = null,
}) {
    const day = entry.day;
    const caption = entry.caption || "";
    const theme = entry.theme || `Day ${day}`;
    const fmt = entry.format || "Post";
    const fmtL = fmt.toLowerCase();
    const isCarousel = /carousel/.test(fmtL);
    const isReel = /reel/.test(fmtL);
    const isStory = /story/.test(fmtL);
    const bestTime = entry.best_time || "";
    const cta = entry.cta || "";
    const shoot = entry.shoot_note || "";
    const [expanded, setExpanded] = useState(false);
    const isLong = caption.length > 240;
    const visibleCaption = (!isLong || expanded) ? caption : caption.slice(0, 220).trimEnd() + "…";

    // Pick the right "primary" media to show in the desktop thumbnail column.
    // Carousel: render strip of slides (multiple). Reel: video. Single: image. Story: nothing (advisory text).
    const hasMedia = isCarousel ? slides.length > 0 : (isReel ? !!reel?.url : !!image?.url);

    // Build the full ready-to-paste text: insert CTA right BEFORE the hashtag block
    // so engagement prompts come naturally before tags. Falls back to appending at the end.
    const fullCaption = (() => {
        if (!cta) return caption;
        if (!caption) return cta;
        const lines = caption.split("\n");
        const hashStart = lines.findIndex(l => /^\s*#/.test(l));
        if (hashStart === -1) {
            return `${caption.trimEnd()}\n\n${cta}`;
        }
        const before = lines.slice(0, hashStart).join("\n").trimEnd();
        const tags = lines.slice(hashStart).join("\n");
        return `${before}\n\n${cta}\n\n${tags}`;
    })();

    const copyCaption = async () => {
        try {
            await navigator.clipboard.writeText(fullCaption);
            toast.success(`Day ${day} caption + CTA copied`, {
                description: cta ? "CTA already included before hashtags. Open IG → long-press → paste." : "Open Instagram → long-press caption field → paste.",
                action: { label: "Open IG", onClick: () => openInstagramCreate() },
                duration: 6000,
            });
        } catch {
            toast.error("Copy failed — long-press the caption to select & copy");
        }
    };

    // One-tap path: copy → IG opens straight to camera/create. The most-clicked button on the card.
    const copyAndOpenIG = async () => {
        try {
            await navigator.clipboard.writeText(fullCaption);
            toast.success(`Day ${day} ready · opening Instagram…`, {
                description: isMobile()
                    ? "IG Camera opens → tap gallery → pick photo → long-press caption → paste."
                    : "On desktop IG won't paste captions — finish this on your phone.",
                duration: 5500,
            });
            setTimeout(() => openInstagramCreate(), 250);
        } catch {
            toast.error("Copy failed — long-press the caption to select & copy");
        }
    };

    const downloadImage = () => {
        if (!image?.url) return;
        saveImage(absUrl(image.url), `day-${day}-${(theme || "post").toLowerCase().replace(/\s+/g, "-")}.png`);
    };

    const downloadAllSlides = () => {
        const items = slides.map((s) => ({
            url: absUrl(s.url),
            filename: `day-${day}-slide-${s.slide || 1}.png`,
        }));
        const zipUrl = orderId ? `${BACKEND}/api/kit/${orderId}/images.zip?day=${day}` : null;
        saveImages(items, zipUrl);
    };

    const downloadReel = () => {
        if (!reel?.url) return;
        saveImage(absUrl(reel.url), `day-${day}-reel.mp4`);
    };

    return (
        <div
            ref={(el) => registerRef && registerRef(day, el)}
            className={`rounded-3xl border bg-cafe-cream p-4 sm:p-6 transition-opacity ${isPosted ? "opacity-60" : ""}`}
            data-testid={`day-card-${day}`}
        >
            {/* Header */}
            <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
                <span className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full ${
                    isPosted ? "bg-cafe-olive text-cafe-cream" : "bg-cafe-espresso text-cafe-cream"
                }`}>
                    Day {day}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-semibold">
                    {fmt}{bestTime ? ` · ${bestTime}` : ""}
                </span>
                {isPosted && (
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-0.5 rounded-full bg-cafe-olive/15 text-cafe-olive border border-cafe-olive/30 inline-flex items-center gap-1">
                        <Check className="w-3 h-3" /> Posted
                    </span>
                )}
            </div>

            {hasMedia && !isCarousel ? (
                /* Single photo OR Reel — small thumb + content side by side */
                <div className="flex sm:grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 sm:gap-6 items-start mb-3 sm:mb-0">
                    <div className="w-20 h-20 sm:w-full sm:h-auto sm:aspect-square rounded-xl sm:rounded-2xl overflow-hidden bg-cafe-latte shrink-0 relative">
                        {isReel && reel?.url ? (
                            <>
                                <video src={absUrl(reel.url)} controls playsInline preload="metadata" className="w-full h-full object-cover" />
                                <span className="absolute top-1.5 left-1.5 bg-cafe-terracotta text-cafe-cream text-[8px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 rounded">Reel</span>
                            </>
                        ) : (
                            <img src={absUrl(image.url)} alt={theme} loading="lazy" className="w-full h-full object-cover" />
                        )}
                    </div>

                    <div className="flex-1 min-w-0 sm:flex sm:flex-col sm:h-full">
                        <h3 className="font-serif text-lg sm:text-2xl text-cafe-espresso leading-tight">{theme}</h3>
                        {entry.hook && (
                            <p className="font-display italic text-cafe-olive text-xs sm:text-sm mt-1 line-clamp-2 sm:line-clamp-none">"{entry.hook}"</p>
                        )}
                        <div className="hidden sm:block mt-3">
                            <div className="bg-cafe-latte/60 rounded-2xl p-3.5 mb-3 max-h-44 overflow-y-auto">
                                <p className="whitespace-pre-line text-cafe-espresso text-sm leading-relaxed">{caption}</p>
                            </div>
                            {cta && (
                                <div className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-xl p-3 mb-3">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-1">💬 Engagement prompt · auto-included on copy</p>
                                    <p className="text-[12px] text-cafe-espresso leading-snug">{cta}</p>
                                </div>
                            )}
                            {shoot && <p className="text-[11px] text-cafe-mocha/80 italic mb-3">📸 {shoot}</p>}
                        </div>
                    </div>
                </div>
            ) : isCarousel && hasMedia ? (
                /* Carousel — full-width 5-slide strip on top, content below */
                <div className="mb-3 sm:mb-0">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="font-serif text-lg sm:text-2xl text-cafe-espresso leading-tight flex-1 min-w-0">{theme}</h3>
                        <span className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-cafe-terracotta font-bold border border-cafe-terracotta/40 rounded-full px-2 py-0.5">
                            Carousel · {slides.length} slide{slides.length > 1 ? "s" : ""}
                        </span>
                    </div>
                    {entry.hook && (
                        <p className="font-display italic text-cafe-olive text-xs sm:text-sm mb-3">"{entry.hook}"</p>
                    )}
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1 scroll-smooth snap-x snap-mandatory" data-testid={`day-${day}-carousel-strip`}>
                        {slides.map((s) => (
                            <div
                                key={s.slide}
                                className="shrink-0 w-32 sm:w-40 aspect-square rounded-xl overflow-hidden bg-cafe-latte snap-start relative"
                            >
                                <img src={absUrl(s.url)} alt={`${theme} slide ${s.slide}`} loading="lazy" className="w-full h-full object-cover" />
                                <span className="absolute top-1 left-1 bg-cafe-espresso/85 text-cafe-cream text-[9px] font-bold px-1.5 py-0.5 rounded">
                                    {s.slide || 1}/{slides.length}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="hidden sm:block">
                        <div className="bg-cafe-latte/60 rounded-2xl p-3.5 mb-3">
                            <p className="whitespace-pre-line text-cafe-espresso text-sm leading-relaxed">{caption}</p>
                        </div>
                        {cta && (
                            <div className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-xl p-3 mb-3">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-1">💬 Engagement prompt · auto-included on copy</p>
                                <p className="text-[12px] text-cafe-espresso leading-snug">{cta}</p>
                            </div>
                        )}
                        {shoot && <p className="text-[11px] text-cafe-mocha/80 italic mb-3">📸 {shoot}</p>}
                    </div>
                </div>
            ) : (
                /* No image (Starter, OR Story-only, OR media still pending): full-width text-only */
                <div className="mb-3 sm:mb-0">
                    <h3 className="font-serif text-xl sm:text-2xl text-cafe-espresso leading-tight">{theme}</h3>
                    {entry.hook && (
                        <p className="font-display italic text-cafe-olive text-sm sm:text-base mt-1.5">"{entry.hook}"</p>
                    )}
                    {isStory && (
                        <div className="mt-3 mb-3 px-4 py-3 rounded-xl border-2 border-dashed border-cafe-mocha/30 bg-cafe-latte/30">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha font-bold mb-1">⚡ Story-only · 24h content</p>
                            <p className="text-[13px] text-cafe-mocha leading-relaxed">
                                Stories disappear after 24 hours so we don't generate them ahead. On the day, snap a quick fresh photo or boomerang and post — keeps your stories feeling live.
                            </p>
                        </div>
                    )}
                    <div className="hidden sm:block mt-4">
                        <div className="bg-cafe-latte/60 rounded-2xl p-4 mb-3">
                            <p className="whitespace-pre-line text-cafe-espresso text-sm leading-relaxed">{caption}</p>
                        </div>
                        {cta && (
                            <div className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-xl p-3.5 mb-3">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-olive font-bold mb-1">💬 Engagement prompt · auto-included when you Copy caption</p>
                                <p className="text-[13px] text-cafe-espresso leading-snug">{cta}</p>
                            </div>
                        )}
                        {shoot && (
                            <div className="mt-3 border-l-2 border-cafe-terracotta/60 bg-cafe-terracotta/5 rounded-r-lg pl-4 pr-3 py-2.5">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-terracotta font-bold mb-1">📸 Shoot this</p>
                                <p className="text-[13px] text-cafe-mocha leading-relaxed">{shoot}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Mobile-only: caption block full width below thumb+title */}
            <div className="sm:hidden">
                <div className="bg-cafe-latte/60 rounded-xl p-3 mb-2">
                    <p className="whitespace-pre-line text-cafe-espresso text-[13px] leading-relaxed">
                        {visibleCaption}
                    </p>
                    {isLong && (
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="mt-1.5 text-xs text-cafe-olive font-semibold underline"
                            data-testid={`day-${day}-toggle-expand`}
                        >
                            {expanded ? "Show less" : "Show more"}
                        </button>
                    )}
                </div>
                {cta && (
                    <div className="bg-cafe-olive/10 border border-cafe-olive/30 rounded-lg p-2.5 mb-2">
                        <p className="text-[9px] uppercase tracking-[0.18em] text-cafe-olive font-bold mb-0.5">💬 Engagement prompt · auto-added on copy</p>
                        <p className="text-[12px] text-cafe-espresso leading-snug">{cta}</p>
                    </div>
                )}
                {shoot && <p className="text-[10px] text-cafe-mocha/80 italic mb-2">📸 {shoot}</p>}
            </div>

            {/* Action buttons */}
            <div className={`flex flex-wrap gap-2 mt-2 sm:mt-auto ${(hasMedia && !isCarousel) ? "sm:pl-[224px]" : ""}`}>
                {isCarousel && slides.length > 0 && (
                    <button
                        onClick={downloadAllSlides}
                        data-testid={`day-${day}-save-all-slides`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-xs font-medium transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" /> Save all {slides.length} slides
                    </button>
                )}
                {!isCarousel && isReel && reel?.url && (
                    <button
                        onClick={downloadReel}
                        data-testid={`day-${day}-save-reel`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-xs font-medium transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" /> Save reel
                    </button>
                )}
                {!isCarousel && !isReel && image?.url && (
                    <button
                        onClick={downloadImage}
                        data-testid={`day-${day}-save-img`}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-xs font-medium transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" /> Save image
                    </button>
                )}
                <button
                    onClick={copyAndOpenIG}
                    data-testid={`day-${day}-copy-open-ig`}
                    title="Copies caption + CTA, then opens Instagram (mobile: jumps to Camera/Create screen — long-press the caption field to paste)"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-terracotta hover:bg-cafe-terracotta/90 text-cafe-cream text-xs font-semibold transition-colors"
                >
                    <InstagramIcon className="w-3.5 h-3.5" /> Copy + Open IG
                </button>
                <button
                    onClick={copyCaption}
                    data-testid={`day-${day}-copy-cap`}
                    title="Copy only — stays on this page"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-cafe-espresso/90 hover:bg-cafe-espresso text-cafe-cream text-xs font-medium transition-colors"
                >
                    <Copy className="w-3.5 h-3.5" /> Copy only
                </button>
                <button
                    onClick={() => onTogglePosted(day)}
                    data-testid={`day-${day}-toggle-posted`}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${
                        isPosted
                            ? "border-cafe-olive bg-cafe-olive/10 text-cafe-olive hover:bg-cafe-olive/20"
                            : "border-border bg-transparent text-cafe-mocha hover:bg-cafe-latte"
                    }`}
                >
                    <Check className="w-3.5 h-3.5" /> {isPosted ? "Posted" : "Mark posted"}
                </button>
            </div>
        </div>
    );
}
